/**
 * CV templates — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_office_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const cvTemplatesPack: ToilePack = {
    slug: 'cv-templates',
    category: 'office',
    name: 'CV templates',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'academic', label: 'Academic', subject: 'a CV with "Education", "Publications", "Research" sections'},
        {key: 'europass', label: 'European style', subject: 'a CV with a left label column and right content column'},
        {key: 'modern', label: 'Modern', subject: 'a CV with a colour header band'},
    ],
    styles: [
        {key: 'plain', label: 'Plain', style: 'plain academic typesetting in a black serif, small caps headings, thin hairline rules', palette: 'black ink on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'charcoal', label: 'Charcoal', style: 'bold contemporary design with a charcoal header block and teal highlights on headings', palette: 'charcoal, teal and white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'cream', label: 'Cream', style: 'warm editorial design on cream paper with a terracotta rule and fine serif headings', palette: 'cream, terracotta and dark brown', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
    ],
    directions: ['navy accent', 'with photo', 'two pages'],
    directionHint: 'try: navy accent',
    seo: {
        title: 'CV templates: academic, European and modern',
        metaDescription: 'Nine printable CV templates: an academic, a European-style and a modern CV, each in a plain, charcoal or cream style.',
        h1: 'CV template: academic, European style, modern',
        tagline: 'Three CV layouts, each in three styles, with grey lines where your own history goes.',
        intro: 'A CV template is the layout a curriculum vitae is built on. These nine show an academic CV with sections for education, publications and research, a European-style CV with a label column, and a modern CV with a colour header band. Each comes in a plain, charcoal or cream style. Only the headings are lettered; grey lines mark your own entries. Type a twist, such as navy accent, and it is a request, not a promised colour.',
        useCases: [
            'an academic or research job application',
            'a job application where a European-style CV is expected',
            'a modern CV with a colour header',
            'a first CV to fill in and print',
        ],
        faq: [
            {q: 'What is the difference between a CV and a resume?', a: 'A CV is usually longer and lists education, publications and research. It is the common term outside the United States. These templates are layouts, so the sections are shown, not written.'},
            {q: 'Are the sections filled in?', a: 'No. Only the headings are lettered. The grey lines show where your entries go.'},
            {q: 'What does the modern CV look like?', a: 'A colour header band runs across the top of the page, and the rest of the page is grey lines for your content.'},
            {q: 'Can I change the accent colour?', a: 'Type it as a twist, such as navy accent. It is a request for that colour, not a promised result.'},
        ],
    },
}
